import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { Endpoints } from "../constants/endpoint";
import useIntervalWhile from "./useIntervalWhile";

// While a session is transcribed again, the backend refuses changes to its
// transcript (409) and replaces the whole transcript once the new one is
// stored. Most saves of the editor do not report errors, so an open editor
// asks every ASK_EVERY_MS and whenever its tab or window comes back, to lock
// itself in time instead of letting edits fail unseen.
export const ASK_EVERY_MS = 10000;

/** Whether the backend is transcribing the session again right now, and the
 * version of its transcript, which changes whenever a job on it ends. */
export const askBeingTranscribed = async (sessionId) => {
  const res = await fetch(
    `${Endpoints.transcriptions}${sessionId}/being-transcribed`
  );
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} asking about session ${sessionId}`);
  }
  const data = await res.json();
  return {
    being: Boolean(data && data.being_transcribed),
    version: data?.transcript_version ?? null,
  };
};

// Why the editor of `sessionId` must stay read-only, or null when it need
// not: "transcribing" while a job works on the transcript, "reloading" while
// the transcript on screen is older than the server's. The latter also
// covers a job that ended unseen, say while the tab was hidden: the rows on
// screen were replaced, and edits to them would be lost.
//
// `loadedBeing` and `loadedVersion` are what the session's last full load
// said; `ask(sessionId)` asks the backend again. When an answer shows a newer
// transcript and no job on it, `onReload()` must reload it; it is called
// again with every answer until the load is current, as a reload can fail.
// `onLocked()` is called when asking finds the open editor being transcribed,
// as its last edits may then have been refused.
export default function useBeingTranscribed(
  sessionId,
  loadedBeing,
  loadedVersion,
  ask,
  onReload,
  onLocked = () => {}
) {
  // The backend's last answer since the last load; null until it answers.
  const [answer, setAnswer] = useState(null);
  const loaded = { being: Boolean(loadedBeing), version: loadedVersion ?? null };
  const askRef = useRef(ask);
  const onReloadRef = useRef(onReload);
  const onLockedRef = useRef(onLocked);
  const sessionRef = useRef(sessionId);
  const loadedRef = useRef(loaded);
  const answerRef = useRef(answer);
  askRef.current = ask;
  onReloadRef.current = onReload;
  onLockedRef.current = onLocked;
  sessionRef.current = sessionId;
  loadedRef.current = loaded;
  answerRef.current = answer;

  // A new load, or another session, starts over from what the load said;
  // before paint, so that a fresh load never shows as out of date.
  useLayoutEffect(() => {
    answerRef.current = null;
    setAnswer(null);
  }, [sessionId, loaded.being, loaded.version]);

  const check = useCallback(async () => {
    const askedAbout = sessionRef.current;
    if (askedAbout == null) return;
    let next;
    try {
      next = await askRef.current(askedAbout);
    } catch (err) {
      return; // No answer is no change: ask again on the next tick.
    }
    // An answer about the session shown before does not apply.
    if (sessionRef.current !== askedAbout || !next) return;
    const wasBeing = (answerRef.current || loadedRef.current).being;
    answerRef.current = next;
    setAnswer(next);
    if (next.being && !wasBeing) onLockedRef.current();
    if (!next.being && next.version !== loadedRef.current.version) {
      onReloadRef.current();
    }
  }, []);

  useIntervalWhile(check, sessionId != null, ASK_EVERY_MS);

  // Back from another tab or window, e.g. the one that started the
  // re-transcription.
  useEffect(() => {
    const onShown = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onShown);
    window.addEventListener("focus", onShown);
    return () => {
      document.removeEventListener("visibilitychange", onShown);
      window.removeEventListener("focus", onShown);
    };
  }, [check]);

  if ((answer || loaded).being) return "transcribing";
  if (answer && answer.version !== loaded.version) return "reloading";
  return null;
}
